// NOTICE: KAPTION-INTEGRATION
// sales-pipeline — state for the configurable Sales Pipeline board: config,
// per-column pages, the chat list's selection (sales-pipeline.HEADER.2), group-by,
// optimistic stage moves and lane reassignments, and live updates from the
// Vuex conversation stream.

import { computed, reactive, ref, unref } from 'vue';
import ConversationApi from 'dashboard/api/inbox/conversation.js';
import KaptionSalesPipelineAPI from 'dashboard/api/kaptionSalesPipeline.js';
import { matchesFilters } from 'dashboard/store/modules/conversations/helpers/filterHelpers';
import {
  GROUP_BY,
  NO_LANE_ID,
  buildFilterPayload,
  buildStages,
  listScopeToFilters,
  matchesBoard,
  matchesClientFilters,
  matchesSearch,
  sortConversations,
  stageIdOf,
} from 'dashboard/helper/kaptionSalesPipeline.js';

export const PIPELINE_PAGE_SIZE = 25;

const emptyColumn = () => ({
  items: [],
  loading: false,
  page: 0,
  hasMore: true,
  total: 0,
  requestId: 0,
});

// listScope: the chat list's selection (ref/computed or plain object).
export function useKaptionSalesPipeline({ listScope = {} } = {}) {
  const config = ref(null);
  const canManage = ref(false);
  const configLoaded = ref(false);
  const configError = ref(false);
  const columns = reactive({});
  // The header (view switcher, chips, status menu) is the only filter source.
  const filters = computed(() => listScopeToFilters(unref(listScope) || {}));
  const sortBy = computed(() => (unref(listScope) || {}).sortBy || null);
  const search = ref('');
  const groupBy = ref(GROUP_BY.NONE);
  // Cards whose move is in flight: a live update carrying the OLD labels must
  // not bounce them back to the source column.
  const pendingMoves = new Set();

  const pipeline = computed(() => config.value?.pipelines?.[0] || null);
  const stages = computed(() => buildStages(pipeline.value));
  const scope = computed(() => pipeline.value?.scope || {});

  const column = stageId => columns[stageId] || emptyColumn();

  const visibleItems = stageId =>
    sortConversations(
      column(stageId).items.filter(
        c =>
          matchesSearch(c, search.value) &&
          matchesClientFilters(c, filters.value, matchesFilters)
      ),
      sortBy.value
    );

  const allVisible = computed(() =>
    stages.value.flatMap(stage => visibleItems(stage.id))
  );

  function resetColumns() {
    Object.keys(columns).forEach(key => {
      delete columns[key];
    });
    stages.value.forEach(stage => {
      columns[stage.id] = emptyColumn();
    });
  }

  function applyConfig(data) {
    config.value = data?.config || null;
    canManage.value = !!data?.can_manage;
    resetColumns();
  }

  async function loadConfig() {
    configError.value = false;
    try {
      const { data } = await KaptionSalesPipelineAPI.show();
      applyConfig(data);
    } catch (error) {
      configError.value = true;
      config.value = null;
    } finally {
      configLoaded.value = true;
    }
  }

  async function fetchColumn(stageId, page = 1) {
    const stage = stages.value.find(s => s.id === stageId);
    if (!stage) return;
    if (!columns[stageId]) columns[stageId] = emptyColumn();
    const col = columns[stageId];
    col.requestId += 1;
    const requestId = col.requestId;
    col.loading = true;
    try {
      const payload = buildFilterPayload({
        stage,
        stages: stages.value,
        scope: scope.value,
        filters: filters.value,
      });
      const { data } = await ConversationApi.filter({
        queryData: { payload },
        page,
      });
      // A newer request (filters changed) owns this column now.
      if (requestId !== col.requestId) return;
      const incoming = data?.payload || [];
      if (page === 1) {
        col.items = incoming;
      } else {
        const seen = new Set(col.items.map(c => c.id));
        col.items = [...col.items, ...incoming.filter(c => !seen.has(c.id))];
      }
      col.page = page;
      col.total = data?.meta?.all_count ?? col.items.length;
      col.hasMore = incoming.length >= PIPELINE_PAGE_SIZE;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[SalesPipeline] fetch ${stageId} failed`, error);
    } finally {
      if (requestId === col.requestId) col.loading = false;
    }
  }

  function fetchAll() {
    return Promise.allSettled(stages.value.map(s => fetchColumn(s.id, 1)));
  }

  function loadMore(stageId) {
    const col = columns[stageId];
    if (!col || !col.hasMore || col.loading) return undefined;
    return fetchColumn(stageId, col.page + 1);
  }

  function locate(conversationId) {
    const stageId = Object.keys(columns).find(id =>
      columns[id].items.some(c => c.id === conversationId)
    );
    if (!stageId) return null;
    const index = columns[stageId].items.findIndex(
      c => c.id === conversationId
    );
    return { stageId, index, item: columns[stageId].items[index] };
  }

  function removeAt(stageId, index) {
    const col = columns[stageId];
    const [item] = col.items.splice(index, 1);
    col.total = Math.max(0, col.total - 1);
    return item;
  }

  function insertTop(stageId, item) {
    const col = columns[stageId];
    if (!col) return;
    col.items.unshift(item);
    col.total += 1;
  }

  /**
   * sales-pipeline.LIVE.1 — fold a conversation pushed through Vuex
   * (ActionCable updateConversation / addConversation) into the board.
   */
  function applyLiveUpdate(conversation) {
    if (!pipeline.value || !conversation?.id) return;
    const found = locate(conversation.id);
    if (pendingMoves.has(conversation.id)) {
      if (found) {
        const { labels, status, ...rest } = conversation;
        Object.assign(found.item, rest);
      }
      return;
    }
    const merged = found ? { ...found.item, ...conversation } : conversation;
    const targetId = stageIdOf(merged, stages.value);
    const target = stages.value.find(s => s.id === targetId);
    const belongs =
      !!target &&
      matchesBoard(
        merged,
        target,
        { scope: scope.value, filters: filters.value },
        matchesFilters
      ) &&
      !!columns[targetId];

    if (found) {
      if (!belongs) {
        removeAt(found.stageId, found.index);
      } else if (found.stageId === targetId) {
        columns[targetId].items.splice(found.index, 1, merged);
      } else {
        removeAt(found.stageId, found.index);
        insertTop(targetId, merged);
      }
    } else if (belongs && columns[targetId].page > 0) {
      insertTop(targetId, merged);
    }
  }

  /**
   * sales-pipeline.MOVE.1 — optimistic move, persisted by ONE server call that
   * swaps stage labels (and resolves when the stage asks) in a transaction.
   */
  async function moveCard(conversation, toStageId) {
    const found = locate(conversation.id);
    if (!found || found.stageId === toStageId || !columns[toStageId]) return;
    const { stageId: fromStageId, index } = found;
    const item = removeAt(fromStageId, index);
    insertTop(toStageId, item);
    pendingMoves.add(item.id);
    let data;
    try {
      ({ data } = await KaptionSalesPipelineAPI.move({
        conversationId: item.id,
        toStageId,
        pipelineId: pipeline.value?.id,
      }));
    } catch (error) {
      pendingMoves.delete(item.id);
      const now = locate(item.id);
      if (now) removeAt(now.stageId, now.index);
      const col = columns[fromStageId];
      col.items.splice(Math.min(index, col.items.length), 0, item);
      col.total += 1;
      throw error;
    }
    pendingMoves.delete(item.id);
    // Server truth (labels + maybe resolved) decides where the card finally sits.
    applyLiveUpdate({ id: item.id, labels: data.labels, status: data.status });
  }

  /**
   * Swimlane drop across lanes: reassign with the existing assignment API
   * (server enforces direct_assignment). Reverted on failure.
   */
  async function reassignCard(conversation, laneId, person) {
    const found = locate(conversation.id);
    const item = found?.item || conversation;
    const isTeam = groupBy.value === GROUP_BY.TEAM;
    const key = isTeam ? 'team' : 'assignee';
    const previous = item.meta?.[key] || null;
    const targetId = laneId === NO_LANE_ID ? 0 : Number(laneId);
    item.meta = {
      ...(item.meta || {}),
      [key]: targetId ? { ...(person || {}), id: targetId } : null,
    };
    try {
      if (isTeam) {
        await ConversationApi.assignTeam({
          conversationId: item.id,
          teamId: targetId,
        });
      } else {
        await ConversationApi.assignAgent({
          conversationId: item.id,
          agentId: targetId,
        });
      }
    } catch (error) {
      item.meta = { ...(item.meta || {}), [key]: previous };
      throw error;
    }
    // Re-check the header filters (e.g. an Assignee chip may now exclude it).
    applyLiveUpdate({ ...item });
  }

  async function saveConfig(nextConfig) {
    const { data } = await KaptionSalesPipelineAPI.save(nextConfig);
    applyConfig(data);
    return data;
  }

  async function createDefault(names) {
    const { data } = await KaptionSalesPipelineAPI.createDefault(names);
    applyConfig(data);
    return data;
  }

  return {
    config,
    canManage,
    configLoaded,
    configError,
    pipeline,
    stages,
    scope,
    columns,
    filters,
    search,
    groupBy,
    column,
    visibleItems,
    allVisible,
    loadConfig,
    fetchColumn,
    fetchAll,
    loadMore,
    moveCard,
    reassignCard,
    applyLiveUpdate,
    saveConfig,
    createDefault,
  };
}
